import Components from "slotworks/components/components";
import Sequence from "slotworks/utils/contracts/sequence";
import Contract from "slotworks/utils/contracts/contract";
import sgScatterParticles from "./matrix/scatter/sg-scatter-particles";
import Layers from "slotworks/graphics/layers/layers";
import Rectangle from "slotworks/utils/geom/rectangle";
import {getOffsetRectangle} from "slotworks/utils/position-offset-calculator";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import {symbolsName} from "sg-consts";
import gameState from "slotworks/model/game-state";
import { Services } from "slotworks/services/services";
import { ExpansionResult } from "slotworks/model/gameplay/records/results/expansion-result";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import Record from "slotworks/model/gameplay/records/record";
import RespinRecord from "model/respin-record";

const SCATTER_INTRO: string = "scatter_intro";
const SCATTER_LOOP: string = "scatter_loop";
const SCATTER_BONUS_TRIGGER: string = "scatter_bonus_trigger";

class SGScatterAnimation {

    private scatterAnimationsInProgress:boolean = false;
    private stopPreviousScatterAnimationSequence:boolean = false;
    private currentScatterAnimationSequence:Contract<void>;

    public init(): void {
        Components.matrix.landSignal.add(this.onReelLand, this);
        Components.matrix.startSignal.add(this.resetAllAnimations, this);
    }

    private onReelLand(index: number): void {
        const record = gameState.getCurrentGame().getCurrentRecord() as Record;
        const expansionResults: ExpansionResult[] = record.getResultsOfType(ExpansionResult);
        switch (index) {
            case 1:
                this.firsReelLand(record);
                break;
            case 4:
                if (this.getScatterLandingIndex() !== -1 && !this.isFreeSpin()) {
                    Services.sound.customEvent("reel_5_anticipate");
                }
                break;
            case 5:
                this.lastReelLand(expansionResults);
                break;
            default:
                break;
        }
    }

    private firsReelLand(record: Record): void {
        if (this.getScatterLandingIndex() !== -1) {
            this.currentScatterAnimationSequence = this.getScatterLandingAnimationContract();
            this.currentScatterAnimationSequence.execute();
            Services.sound.customEvent("scatter");
        } else if (record instanceof RespinRecord || record instanceof FreespinRecord) {
            Services.sound.customEvent("near_miss");
            if (!this.isFreeSpin()) {
                Services.sound.customEvent("base-game-sound-decrease");
            } else {
                Services.sound.customEvent("bonus-game-sound-decrease");
            }
        }
    }

    private lastReelLand(expansionResults: ExpansionResult[]): void {
        if (this.getScatterLandingIndex() !== -1 && expansionResults.length > 0 && !this.isFreeSpin()) {
            Services.sound.customEvent("reel_5_landing");
        } else if(this.getScatterLandingIndex() !== -1 && expansionResults.length === 0) {
            Services.sound.customEvent("near_miss");
            if (!this.isFreeSpin()) {
                Services.sound.customEvent("base-game-sound-normalise");
            } else {
                Services.sound.customEvent("bonus-game-sound-normalise");
            }
        }
    }

    public resetAllAnimations(): void {
        this.stopPreviousScatterAnimationSequence = false;
        if(this.scatterAnimationsInProgress){
            this.stopPreviousScatterAnimationSequence = true;
        }
        this.resetAnimation(SCATTER_INTRO);
        this.resetAnimation(SCATTER_LOOP);
        this.resetAnimation(SCATTER_BONUS_TRIGGER);
    }

    private getScatterLandingAnimationContract(): Contract<void> {
        let scatterSymbolIndex = this.getScatterLandingIndex();
        return new Sequence([
            () => Contract.wrap(()=>{
                this.scatterAnimationsInProgress = true;
            }),
            () => new Sequence([
                () => sgScatterParticles.playLandingParticlesAnimation(scatterSymbolIndex),
                () => this.playScatterIntroAnimation(scatterSymbolIndex)
            ]),
            () => Contract.wrap(() => {
                this.playScatterLoopAnimation(scatterSymbolIndex);
            }),
            () => Contract.wrap(() => {
                this.scatterAnimationsInProgress = false;
            })
        ]);
    }

    public getScatterBonusTriggerAnimationContract(): Contract<void> {
        let scatterSymbolIndex = this.getScatterLandingIndex();
        this.resetAnimation(SCATTER_INTRO);
        this.resetAnimation(SCATTER_LOOP);
        return new Sequence([
            () => this.playScatterBonusTriggerAnimation(scatterSymbolIndex),
            () => Contract.wrap(() => {
                this.resetAnimation(SCATTER_BONUS_TRIGGER);
                this.setScatterSymbolOnReelVisible(scatterSymbolIndex, true);
            })
        ]);
    }

    private playScatterIntroAnimation(cell: number): Contract<void> {
        if(this.stopPreviousScatterAnimationSequence){
            return Contract.empty();
        }
        this.setScatterSymbolOnReelVisible(cell, false);
        const animation = this.getScatterAnimation(SCATTER_INTRO);
        this.updateScatterPosition(cell, animation);
        return animation.gotoAndPlayOnce();
    };

    private playScatterLoopAnimation(cell: number): void {
        if(this.stopPreviousScatterAnimationSequence){
            return;
        }
        this.resetAnimation(SCATTER_INTRO);
        this.setScatterSymbolOnReelVisible(cell, false);
        const animation = this.getScatterAnimation(SCATTER_LOOP);
        this.updateScatterPosition(cell, animation);
        animation.loop = true;
        animation.gotoAndPlay(0);
    };

    private playScatterBonusTriggerAnimation(cell: number): Contract<void> {
        this.setScatterSymbolOnReelVisible(cell, false);
        const animation = this.getScatterAnimation(SCATTER_BONUS_TRIGGER);
        this.updateScatterPosition(cell, animation);
        return animation.gotoAndPlayOnce();
    };

    private setScatterSymbolOnReelVisible(cell: number, value: boolean): void {
        let scatterSymbol = Components.matrix.getSymbol(0, cell - 1);
        scatterSymbol.setVisible(value);
    }

    private resetAnimation(animationId: string): void {
        const animation = Layers.SymbolAnimations.getAnimatedSprite(animationId);
        animation.gotoAndStop(0);
        animation.visible = false;
    }

    private getScatterAnimation(animationId: string): AnimatedSprite {
        const scatterAnimation = Layers.SymbolAnimations.getAnimatedSprite(animationId);
        scatterAnimation.visible = true;
        return scatterAnimation;
    }

    private updateScatterPosition(cell: number, target: AnimatedSprite): void {
        const offset: Rectangle = getOffsetRectangle(Layers.MatrixContent, "symbol_1_1", "scatter_landing");
        let symbolPosition: Rectangle = Layers.MatrixContent.getPosition("symbol_1_" + cell);
        target.x = symbolPosition.x + offset.x;
        target.y = symbolPosition.y + offset.y;
    }

    public getScatterLandingIndex(): number {
        let index = -1;

        const record: SpinRecord = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        const reelset = record.grid[0];

        for (let i = 0; i < 3; i++) {
            let symbol = reelset[i];
            if (symbol === symbolsName.SCATTER) {
                index = i + 1;
                break;
            }
        }
        return index;
    }

    private isFreeSpin(): boolean {
        const record = gameState.getCurrentGame().getCurrentRecord();
        return record instanceof FreespinRecord;
    }    
}

export default new SGScatterAnimation();
